import classNames from 'classnames';
import { FormikProvider, useFormik } from 'formik';
import lowerCase from 'lodash/lowerCase';
import pluralize from 'pluralize';
import { MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactQuill, { Quill } from 'react-quill-new';
import { Link, useSearchParams } from 'react-router-dom';
import * as yup from 'yup';
import { CertificatesSelectPaginate } from '@/modules/common/components/certificates/CertificatesSelectPaginate.tsx';
import { REGISTER_ID_SEARCH_PARAM_KEY } from '@/modules/common/constants/search-params.ts';
import { preventFormDefault } from '@/modules/common/utils/preventFormDefault.ts';
import {
  useCurrentUserRegistersQuery,
  useRegisterClassListInfinityQuery,
} from '@/modules/investor-dashboard/queries/registers.tsx';
import { useRegisterShareholderList } from '@/modules/investor-dashboard/queries/shareholders.tsx';
import ReportsSelectPaginate, {
  ReportOption,
} from '@/modules/issuer-dashboard/components/ReportsSelectPaginate.tsx';
import {
  isGeneratedReportConfigComplete,
  onlyPdfReportTypes,
} from '@/modules/issuer-dashboard/components/communications/IssuerCommunicationBulkReportConfig.tsx';
import { IssuerCommunicationConfirmation } from '@/modules/issuer-dashboard/components/communications/IssuerCommunicationConfirmation.tsx';
import { IssuerCommunicationGeneratedAttachmentsConfig } from '@/modules/issuer-dashboard/components/communications/IssuerCommunicationGeneratedAttachmentsConfig.tsx';
import {
  CommunicationFormValues,
  CommunicationPayload,
  buildCommunicationPayload,
} from '@/modules/issuer-dashboard/helpers/buildCommunicationPayload.ts';
import { getTaxReportYearOptions } from '@/modules/issuer-dashboard/helpers/taxReportYears.ts';
import {
  useIssuerAddAnnouncementMutation,
  usePreviewCommunicationGeneratedAttachmentsMutation,
} from '@/modules/issuer-dashboard/queries/communications.ts';
import { useShareholdersByIdsSubscriptionsClasses } from '@/modules/issuer-dashboard/queries/shareholders.tsx';
import { issuerDashboardPaths } from '@/modules/issuer-dashboard/routes.tsx';
import {
  Alert,
  BaseModalProps,
  Button,
  CalendarInputProps,
  CustomCheckbox,
  ModalContentWrapper,
  Select,
  SelectOption,
  SelectPaginate,
  SelectPaginateOption,
  TextInputField,
  TooltipNew,
} from 'ui';
import { AlertBlueIcon, InfoCircleIcon } from 'ui/icons';
import { replaceQuillSizeOptionIcons } from 'ui/utils';
import {
  BulkReportType,
  EntityPreferredContactWay,
  ReportFormat,
  ShareCertificateResponse,
} from 'api';
import type { CommunicationAttachmentPreviewResponse } from 'api';
import 'react-quill/dist/quill.snow.css';

const validationSchema = yup.object({
  subject: yup.string().trim().min(5).max(50).required('This field is required'),
  text: yup.string().trim(),
  shareholder_ids: yup
    .array()
    .min(1, 'Select at least one holder')
    .required('This field is required'),
});

const initialValues: CommunicationFormValues = {
  subject: '',
  text: '',
  report_ids: [],
  certificate_ids: [],
  shareholder_ids: [],
  bulk_generate_attachments: false,
  generate_report: false,
  generate_certificate: false,
  bulk_report_config: {
    report_type: BulkReportType.HoldingStatement,
    register_class_ids: [],
    start_date: null,
    end_date: null,
    financial_year: null,
    dividend_event_id: undefined,
    file_type: ReportFormat.Pdf,
  },
  bulk_certificate_config: {
    register_class_ids: [],
    file_type: ReportFormat.Pdf,
  },
};

const FontSizeStyle = Quill.import('attributors/style/size') as any;

FontSizeStyle.whitelist = ['10px', '14px', '18px', '24px', '32px'];
Quill.register(FontSizeStyle, true);

const formats = [
  'bold',
  'italic',
  'underline',
  'strike',
  'color',
  'background',
  'size',
  'script',
  'list',
  // 'bullet',
  'link',
];

const quillModules = {
  toolbar: [
    ['bold', 'italic', 'underline', 'strike'],
    [{ size: '24px' }, { size: '32px' }],
    [{ color: [] }, { background: [] }],
    [{ script: 'sub' }, { script: 'super' }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
  ],
};

const allClassesOption = { value: 0, label: 'All classes' };
const HOLDER_NAME_VARIABLE = '{{HOLDER_NAME}}';
const taxReportYearOptions = getTaxReportYearOptions(11);

const formatCommunicationText = (text: string) => `
  <div style="text-align: initial">
    ${text
      .replaceAll('<p>', "<p style='margin: 0'>")
      .replaceAll('<h1>', "<h1 style='margin: 0'>")
      .replaceAll('<h2>', "<h2 style='margin: 0'>")
      .replaceAll('<ul>', "<ul style='margin: 0'>")
      .replaceAll('<li>', "<li style='margin: 0'>")}
  </div>
`;

const getHolderEmailIneligibilityReason = (holder: {
  additional_emails?: string[];
  email?: string;
  notifications_enabled?: boolean;
  preferred_contact_way?: EntityPreferredContactWay;
}) => {
  const reasons: string[] = [];

  if (holder.notifications_enabled === false) {
    reasons.push('Email notifications are disabled');
  }

  if (holder.preferred_contact_way !== EntityPreferredContactWay.Email) {
    reasons.push('Preferred communication method is not Email');
  }

  if (!holder.email && !holder.additional_emails?.length) {
    reasons.push('Email address is missing');
  }

  return reasons.length ? reasons.join('. ') : null;
};

export interface IssuerAddAnnouncementModalProps extends BaseModalProps {
  reports?: ReportOption[];
  certificates?: SelectPaginateOption<ShareCertificateResponse>[];
  registerClass?: SelectPaginateOption<number> | null;
}

export const IssuerAddAnnouncementModal = ({
  open,
  onClose,
  registerClass = allClassesOption,
  reports = [],
  certificates = [],
}: IssuerAddAnnouncementModalProps) => {
  const [params, setParams] = useSearchParams();
  const registerId = +(params.get(REGISTER_ID_SEARCH_PARAM_KEY) || '0');
  const [selectedReports, setSelectedReports] = useState(reports);
  const [selectedCertificates, setSelectedCertificates] = useState(certificates);
  const [selectedClass, setSelectedClass] = useState<SelectPaginateOption<number> | null>(
    registerClass,
  );
  const [classToAddId, setClassToAddId] = useState<number | null>(null);
  const [selectedBulkReportClasses, setSelectedBulkReportClasses] = useState<
    SelectPaginateOption<number>[]
  >([]);
  const [selectedDividendEvent, setSelectedDividendEvent] =
    useState<SelectPaginateOption<number> | null>(null);
  const [selectedBulkCertificateClasses, setSelectedBulkCertificateClasses] = useState<
    SelectPaginateOption<number>[]
  >([]);
  const [generatedAttachmentsError, setGeneratedAttachmentsError] = useState<string | null>(null);
  const [communicationPreview, setCommunicationPreview] =
    useState<CommunicationAttachmentPreviewResponse | null>(null);
  const [step, setStep] = useState(1);
  const quillRef = useRef<ReactQuill | null>(null);
  const lastFocusedFieldRef = useRef<'subject' | 'body'>('body');
  const placeholderTargetFieldRef = useRef<'subject' | 'body' | null>(null);
  const subjectSelectionRef = useRef<{ start: number; end: number } | null>(null);
  const lastQuillSelectionRef = useRef<{ index: number; length: number } | null>(null);

  const resetToEmailStep = useCallback(() => {
    setStep(1);
  }, []);

  const buildPayloadFromValues = useCallback(
    (values: CommunicationFormValues) =>
      buildCommunicationPayload(
        values,
        selectedReports,
        selectedCertificates,
        formatCommunicationText(values.text),
      ),
    [selectedCertificates, selectedReports],
  );

  const formik = useFormik({
    initialValues,
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    onSubmit: async values => {
      const payload = buildPayloadFromValues(values);
      await handleSubmit(payload);
    },
    enableReinitialize: true,
  });
  const { setFieldValue, validateField } = formik;

  const handleSubmit = async (values: CommunicationPayload) => {
    await addAnnouncementEvent(values as Parameters<typeof addAnnouncementEvent>[0]);
    formik.resetForm();
    resetToEmailStep();
    setSelectedClass(allClassesOption);
    setClassToAddId(null);
    setCommunicationPreview(null);
    setSelectedBulkReportClasses([]);
    setSelectedBulkCertificateClasses([]);
    setSelectedDividendEvent(null);
    setGeneratedAttachmentsError(null);
    onClose();
  };

  const { data: registerHoldersData } = useRegisterShareholderList({
    register_id: registerId,
    is_subscribed: true,
  });

  const registerHolders = useMemo(
    () => registerHoldersData?.shareholders || [],
    [registerHoldersData?.shareholders],
  );
  const eligibleRegisterHolders = useMemo(
    () => registerHolders.filter(holder => !getHolderEmailIneligibilityReason(holder)),
    [registerHolders],
  );

  const checkedAllHoldersCheckbox =
    formik.values.shareholder_ids.length === eligibleRegisterHolders.length &&
    formik.values.shareholder_ids.length > 0;
  const indeterminateAllHoldersCheckbox =
    !checkedAllHoldersCheckbox && !!formik.values.shareholder_ids.length;

  const { isFetching: isClassHoldersLoading, data: classHoldersData } = useRegisterShareholderList(
    {
      register_id: registerId,
      register_class_id: classToAddId || undefined,
      is_subscribed: true,
    },
    { disabled: !classToAddId },
  );

  const {
    data: holdersSubscriptions,
  } = // todo: get rid of it?
    useShareholdersByIdsSubscriptionsClasses(
      { shareholders_ids: formik.values.shareholder_ids.toString() },
      !formik.values.shareholder_ids.length,
    );

  const handleChangeClass = useCallback(
    async (item: SelectPaginateOption<number> | null) => {
      const nextClass = item || allClassesOption;

      setSelectedClass(nextClass);

      if (nextClass.value === 0) {
        setClassToAddId(null);
        await setFieldValue('shareholder_ids', []);
        await validateField('shareholder_ids');
        return;
      }

      setClassToAddId(nextClass.value);
    },
    [setFieldValue, validateField],
  );

  const { data: registersResponse, isLoading: isRegistersLoading } =
    useCurrentUserRegistersQuery(true);

  const { fetchNextPage: fetchNextRegisterClassListPage } = useRegisterClassListInfinityQuery(
    { page: 1, page_size: 10, register_id: registerId },
    true,
  );

  const { mutateAsync: addAnnouncementEvent, isLoading: isAnnouncementCreation } =
    useIssuerAddAnnouncementMutation({
      setErrors: formik.setErrors,
    });
  const {
    mutateAsync: previewCommunicationGeneratedAttachments,
    isLoading: isCommunicationPreviewLoading,
  } = usePreviewCommunicationGeneratedAttachmentsMutation();

  const registerOptions = useMemo<SelectOption<number>[]>(() => {
    if (!registersResponse?.data) return [];

    return (
      registersResponse?.data.map(({ register_id, register_obj }) => ({
        label: register_obj?.name,
        value: register_id,
      })) || []
    );
  }, [registersResponse]);

  const handleAsyncRegisterClassList = useCallback(
    async (currentPage: number, search?: string) => {
      const response = await fetchNextRegisterClassListPage({
        pageParam: {
          page: currentPage,
          search,
        },
      });

      return response.data?.pages.reduce((acc: any, { data, page }) => {
        if (page !== currentPage) {
          return acc;
        }

        return data.map(({ class_name, id }) => ({
          label: class_name,
          value: id,
        }));
      }, []);
    },
    [fetchNextRegisterClassListPage],
  );

  const handleChangeRegister = useCallback(
    (register_id: number | null) => {
      if (!register_id) return;

      formik.setFieldValue('register_class_id', null);
      formik.setFieldValue('shareholder_ids', []);
      formik.setFieldValue('generate_report', false);
      formik.setFieldValue('generate_certificate', false);
      formik.setFieldValue('bulk_report_config', initialValues.bulk_report_config);
      formik.setFieldValue('bulk_certificate_config', initialValues.bulk_certificate_config);
      setCommunicationPreview(null);
      setSelectedClass(allClassesOption);
      setClassToAddId(null);
      setSelectedBulkReportClasses([]);
      setSelectedBulkCertificateClasses([]);
      setSelectedDividendEvent(null);
      setGeneratedAttachmentsError(null);
      setSelectedReports([]);
      setSelectedCertificates([]);

      setParams(params => {
        params.set(REGISTER_ID_SEARCH_PARAM_KEY, register_id?.toString());
        return params;
      });
    },
    [formik, setParams],
  );

  const handleCloseModalWithResetForm = useCallback(() => {
    formik.resetForm();
    resetToEmailStep();
    setSelectedClass(allClassesOption);
    setClassToAddId(null);
    setCommunicationPreview(null);
    setSelectedBulkReportClasses([]);
    setSelectedBulkCertificateClasses([]);
    setSelectedDividendEvent(null);
    setGeneratedAttachmentsError(null);
    onClose();
  }, [formik, onClose, resetToEmailStep]);

  const handleToggleBulkGenerateReports = useCallback(
    async (checked: boolean) => {
      resetToEmailStep();
      setGeneratedAttachmentsError(null);
      await formik.setFieldValue('bulk_generate_attachments', checked);
      if (!checked) {
        setCommunicationPreview(null);
      }
    },
    [formik, resetToEmailStep],
  );

  const handleChangeBulkReportClasses = useCallback(
    async (items: SelectPaginateOption<number>[]) => {
      setGeneratedAttachmentsError(null);
      setSelectedBulkReportClasses(items);
      await formik.setFieldValue(
        'bulk_report_config.register_class_ids',
        items.map(({ value }) => value),
      );
    },
    [formik],
  );

  const handleChangeBulkReportDateRange: CalendarInputProps['rangeOnChange'] = useCallback(
    (dates: Parameters<NonNullable<CalendarInputProps['rangeOnChange']>>[0]) => {
      setGeneratedAttachmentsError(null);
      formik.setFieldValue(
        'bulk_report_config.start_date',
        dates?.[0]?.format('YYYY-MM-DD') || null,
      );
      formik.setFieldValue('bulk_report_config.end_date', dates?.[1]?.format('YYYY-MM-DD') || null);
    },
    [formik],
  );

  const handleChangeBulkReportEndDate: CalendarInputProps['onChange'] = useCallback(
    (date: Parameters<NonNullable<CalendarInputProps['onChange']>>[0]) => {
      setGeneratedAttachmentsError(null);
      formik.setFieldValue('bulk_report_config.start_date', null);
      formik.setFieldValue('bulk_report_config.end_date', date?.format('YYYY-MM-DD') || null);
    },
    [formik],
  );

  const handleChangeBulkReportType = useCallback(
    (value: BulkReportType) => {
      setGeneratedAttachmentsError(null);
      formik.setFieldValue('bulk_report_config.report_type', value);
      formik.setFieldValue('bulk_report_config.register_class_ids', []);
      formik.setFieldValue('bulk_report_config.start_date', null);
      formik.setFieldValue('bulk_report_config.end_date', null);
      formik.setFieldValue(
        'bulk_report_config.financial_year',
        value === BulkReportType.TaxReturn ? taxReportYearOptions[0]?.value || null : null,
      );
      formik.setFieldValue('bulk_report_config.dividend_event_id', undefined);
      formik.setFieldValue(
        'bulk_report_config.file_type',
        onlyPdfReportTypes.includes(value)
          ? ReportFormat.Pdf
          : formik.values.bulk_report_config.file_type,
      );
      setSelectedBulkReportClasses([]);
      setSelectedDividendEvent(null);
    },
    [formik],
  );

  const handleChangeBulkReportFinancialYear = useCallback(
    (value: number | null) => {
      setGeneratedAttachmentsError(null);
      formik.setFieldValue('bulk_report_config.financial_year', value || null);
    },
    [formik],
  );

  const handleChangeBulkReportDividendEvent = useCallback(
    (value: SelectPaginateOption<number> | null) => {
      setGeneratedAttachmentsError(null);
      setSelectedDividendEvent(value);
      formik.setFieldValue('bulk_report_config.dividend_event_id', value?.value);
    },
    [formik],
  );

  const handleChangeBulkReportFileType = useCallback(
    (value: ReportFormat) => {
      setGeneratedAttachmentsError(null);
      formik.setFieldValue('bulk_report_config.file_type', value);
    },
    [formik],
  );

  const handleToggleGenerateReport = useCallback(
    (checked: boolean) => {
      setGeneratedAttachmentsError(null);
      formik.setFieldValue('generate_report', checked);
    },
    [formik],
  );

  const handleToggleGenerateCertificate = useCallback(
    (checked: boolean) => {
      setGeneratedAttachmentsError(null);
      formik.setFieldValue('generate_certificate', checked);
    },
    [formik],
  );

  const handleChangeBulkCertificateClasses = useCallback(
    async (items: SelectPaginateOption<number>[]) => {
      setGeneratedAttachmentsError(null);
      setSelectedBulkCertificateClasses(items);
      await formik.setFieldValue(
        'bulk_certificate_config.register_class_ids',
        items.map(({ value }) => value),
      );
    },
    [formik],
  );

  const validateGeneratedAttachments = useCallback(() => {
    if (!formik.values.bulk_generate_attachments) return true;

    if (!formik.values.generate_report && !formik.values.generate_certificate) {
      setGeneratedAttachmentsError('Select report, certificate, or both.');
      return false;
    }

    if (formik.values.generate_report && !isGeneratedReportConfigComplete(formik.values)) {
      setGeneratedAttachmentsError('Complete generated report configuration.');
      return false;
    }

    if (
      formik.values.generate_certificate &&
      !formik.values.bulk_certificate_config.register_class_ids.length
    ) {
      setGeneratedAttachmentsError('Select certificate class.');
      return false;
    }

    setGeneratedAttachmentsError(null);
    return true;
  }, [formik.values]);

  const handlePrimaryAction = useCallback(async () => {
    if (step === 1) {
      const errors = await formik.validateForm();

      if (errors.subject || errors.shareholder_ids) {
        await formik.setTouched({
          subject: true,
          shareholder_ids: true,
        });
        return;
      }

      if (formik.values.bulk_generate_attachments && !validateGeneratedAttachments()) return;

      try {
        const preview = await previewCommunicationGeneratedAttachments(
          buildPayloadFromValues(formik.values),
        );

        setCommunicationPreview(preview);
      } catch {
        return;
      }

      setStep(2);
      return;
    }

    await formik.submitForm();
  }, [
    buildPayloadFromValues,
    formik,
    previewCommunicationGeneratedAttachments,
    step,
    validateGeneratedAttachments,
  ]);

  const handleChangeSubject = useCallback(
    async (value: string) => {
      await formik.setFieldValue('subject', value, true);
    },
    [formik],
  );

  const handleChangeText = useCallback(
    (value: string) => {
      formik.setFieldValue('text', value);
    },
    [formik],
  );

  const handleChangeTextSelection = useCallback(
    (range: { index: number; length: number } | null) => {
      if (range) {
        lastQuillSelectionRef.current = range;
      }
    },
    [],
  );

  const saveCurrentQuillSelection = useCallback(() => {
    const editor = quillRef.current?.getEditor();
    const selection = editor?.getSelection();

    if (selection) {
      lastQuillSelectionRef.current = {
        index: selection.index,
        length: selection.length,
      };
    }
  }, []);

  const saveSubjectSelection = useCallback((input: HTMLInputElement) => {
    lastFocusedFieldRef.current = 'subject';

    subjectSelectionRef.current = {
      start: input.selectionStart ?? input.value.length,
      end: input.selectionEnd ?? input.value.length,
    };
  }, []);

  const insertHolderNameToBody = useCallback(() => {
    const editor = quillRef.current?.getEditor();

    if (!editor) {
      formik.setFieldValue('text', `${formik.values.text}${HOLDER_NAME_VARIABLE}`);
      return;
    }

    const savedRange = lastQuillSelectionRef.current;
    const editorEndIndex = Math.max(editor.getLength() - 1, 0);
    const index = savedRange ? Math.min(savedRange.index, editorEndIndex) : editorEndIndex;

    editor.focus();
    if (savedRange?.length) {
      editor.deleteText(index, savedRange.length, 'user');
    }
    editor.insertText(index, HOLDER_NAME_VARIABLE, 'user');
    editor.setSelection(index + HOLDER_NAME_VARIABLE.length, 0, 'silent');
  }, [formik]);

  const insertHolderNameToSubject = useCallback(() => {
    const subject = formik.values.subject || '';
    const selection = subjectSelectionRef.current;
    const start = Math.min(selection?.start ?? subject.length, subject.length);
    const end = Math.min(selection?.end ?? start, subject.length);
    const nextSubject = `${subject.slice(0, start)}${HOLDER_NAME_VARIABLE}${subject.slice(end)}`;
    const nextCursorPosition = start + HOLDER_NAME_VARIABLE.length;

    formik.setFieldValue('subject', nextSubject, true);
    lastFocusedFieldRef.current = 'subject';

    requestAnimationFrame(() => {
      const input = document.querySelector<HTMLInputElement>('input[name="subject"]');

      input?.focus();
      input?.setSelectionRange(nextCursorPosition, nextCursorPosition);

      subjectSelectionRef.current = {
        start: nextCursorPosition,
        end: nextCursorPosition,
      };
    });
  }, [formik]);

  const handleInsertHolderNamePlaceholder = useCallback(() => {
    const targetField = placeholderTargetFieldRef.current || lastFocusedFieldRef.current;
    placeholderTargetFieldRef.current = null;

    if (targetField === 'subject') {
      insertHolderNameToSubject();
      return;
    }

    insertHolderNameToBody();
  }, [insertHolderNameToBody, insertHolderNameToSubject]);

  const handlePlaceholderMouseDown = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();

      const activeElement = document.activeElement as HTMLInputElement | null;
      const isSubjectActive = activeElement?.getAttribute('name') === 'subject';

      if (isSubjectActive) {
        saveSubjectSelection(activeElement);
        placeholderTargetFieldRef.current = 'subject';
        return;
      }

      placeholderTargetFieldRef.current = lastFocusedFieldRef.current;

      if (placeholderTargetFieldRef.current === 'body') {
        saveCurrentQuillSelection();
      }
    },
    [saveCurrentQuillSelection, saveSubjectSelection],
  );

  const handleToggleAllHoldersSelection = useCallback(
    async (checked: boolean) => {
      setSelectedClass(allClassesOption);
      setClassToAddId(null);

      const newHolders = checked ? eligibleRegisterHolders.map(({ id }) => id) : [];

      await setFieldValue('shareholder_ids', newHolders);
      await validateField('shareholder_ids');
    },
    [eligibleRegisterHolders, setFieldValue, validateField],
  );

  const handleToggleHolderSelection = useCallback(
    async (id: number, checked: boolean) => {
      const newHolders = checked
        ? [...formik.values.shareholder_ids, id]
        : formik.values.shareholder_ids.filter(idx => idx !== id);

      await setFieldValue('shareholder_ids', newHolders);
      await validateField('shareholder_ids');
    },
    [formik.values.shareholder_ids, setFieldValue, validateField],
  );

  const announcementHolders = useMemo(
    () =>
      registerHoldersData?.shareholders.filter(({ id }) =>
        formik.values.shareholder_ids.includes(id),
      ),
    [formik.values.shareholder_ids, registerHoldersData?.shareholders],
  );

  const holderSecurityWarning = useMemo(() => {
    if (!selectedReports?.length || !formik.values?.shareholder_ids || !announcementHolders)
      return null;

    for (const report of selectedReports) {
      if (!report.value.entity_id) continue;

      const holder = registerHoldersData?.shareholders.find(
        ({ entity_id }) => entity_id === report.value.entity_id,
      );
      const foreignHolders = announcementHolders.filter(
        ({ entity_id }) => entity_id !== report.value.entity_id,
      );

      if (!holder || !foreignHolders.length) continue;

      return `
       You have selected a ${lowerCase(report.value.type)} report "${report.label}" for holder ${
        holder.entity.entity_name
      } but in recipients you have ${
        foreignHolders.length === 1
          ? `${foreignHolders[0].entity.entity_name} holder`
          : `${foreignHolders.length} more holders`
      }. It may be private information, are you sure you want to share this?
     `;
    }

    return null;
  }, [
    announcementHolders,
    formik.values?.shareholder_ids,
    registerHoldersData?.shareholders,
    selectedReports,
  ]);

  const classSecurityWarning = useMemo(() => {
    if (!selectedReports?.length || !holdersSubscriptions?.shareholders?.length) return null;

    for (const holder of holdersSubscriptions.shareholders) {
      for (const report of selectedReports) {
        if (!report.value.classes.length) continue;

        const foreignClass = report.value.classes.find(
          rc => !holder.classes.some(hc => hc.id === rc.id),
        );

        if (foreignClass) {
          return `You want to send a report "${report.value.name}",
           which contains information about class ${foreignClass.class_name},
           to holder ${holder.entity.entity_name},
           who is not subscribed to class ${foreignClass.class_name}.`;
        }
      }
    }

    return null;
  }, [holdersSubscriptions?.shareholders, selectedReports]);

  const certificatesSecurityWarning = useMemo<string | null>(() => {
    if (
      !selectedCertificates?.length ||
      !formik.values?.shareholder_ids ||
      !announcementHolders?.length
    ) {
      return null;
    }

    for (const cert of selectedCertificates) {
      if (!cert.value.entity?.id) continue;

      const holder = registerHoldersData?.shareholders.find(
        ({ entity_id }) => entity_id === cert.value.entity?.id,
      );
      const foreignHolders = announcementHolders.filter(
        ({ entity_id }) => entity_id !== cert.value.entity?.id,
      );

      if (!holder || !foreignHolders.length) continue;

      return `
       You have selected a certificate that belongs to "${
         cert.value.entity?.entity_name || cert.value.register_data?.name
       }" but in recipients you have ${
        foreignHolders.length === 1
          ? `${foreignHolders[0].entity.entity_name} holder`
          : `${foreignHolders.length} more holders`
      }. It may be private information, are you sure you want to share this?
     `;
    }

    return null;
  }, [
    announcementHolders,
    formik.values?.shareholder_ids,
    registerHoldersData?.shareholders,
    selectedCertificates,
  ]);

  const securityWarning = !formik.values.bulk_generate_attachments
    ? holderSecurityWarning || classSecurityWarning || certificatesSecurityWarning
    : null;

  useEffect(() => {
    replaceQuillSizeOptionIcons();
  }, []);

  useEffect(() => {
    if (!classToAddId || isClassHoldersLoading || !classHoldersData) return;

    const classHolderIds = classHoldersData.shareholders
      .filter(holder => !getHolderEmailIneligibilityReason(holder))
      .map(holder => holder.id);

    setClassToAddId(null);

    void setFieldValue('shareholder_ids', classHolderIds);
    void validateField('shareholder_ids');
  }, [classHoldersData, classToAddId, isClassHoldersLoading, setFieldValue, validateField]);

  return (
    <ModalContentWrapper
      open={open}
      title="New Email"
      onClose={handleCloseModalWithResetForm}
      classes={{
        dialogPanel: 'lg:!max-w-5xl',
      }}
    >
      <FormikProvider value={formik}>
        <form className="space-y-4" onSubmit={preventFormDefault}>
          {step === 2 && communicationPreview ? (
            <IssuerCommunicationConfirmation
              preview={communicationPreview}
              isSubmitting={isAnnouncementCreation}
              onBack={() => setStep(1)}
              onConfirm={formik.submitForm}
            />
          ) : (
            <div className="grid gap-4 lg:grid-cols-2 lg:gap-0">
              <div className="space-y-4 lg:pr-6 lg:border-r">
                <TextInputField
                  label="Subject"
                  name="subject"
                  placeholder="Some subject"
                  onFocus={event => saveSubjectSelection(event.currentTarget)}
                  onClick={event => saveSubjectSelection(event.currentTarget)}
                  onKeyUp={event => saveSubjectSelection(event.currentTarget)}
                  onSelect={event => saveSubjectSelection(event.currentTarget)}
                  onChange={e => handleChangeSubject(e.target.value)}
                />
                <Alert icon={AlertBlueIcon} variant="neutral-2">
                  <div className="whitespace-normal">
                    {'{{HOLDER_NAME}}'} will be replaced with the holder&apos;s actual name.
                  </div>
                </Alert>
                <div className="border border-gray-300">
                  <div className="flex items-center px-5 py-2">
                    <Button
                      type="button"
                      variant="secondary"
                      className="w-fit min-w-0 min-h-0 px-2 py-1 text-xs leading-4 rounded-sm text-blue-700 hover:bg-blue-700/[.06] focus:bg-blue-700/[.12]"
                      onMouseDown={handlePlaceholderMouseDown}
                      onClick={handleInsertHolderNamePlaceholder}
                    >
                      {'{ }'} Insert Holder Name
                    </Button>
                  </div>
                  <div onKeyUp={saveCurrentQuillSelection} onMouseUp={saveCurrentQuillSelection}>
                    <ReactQuill
                      ref={quillRef}
                      value={formik.values.text}
                      modules={quillModules}
                      formats={formats}
                      theme="snow"
                      onFocus={() => {
                        lastFocusedFieldRef.current = 'body';
                        saveCurrentQuillSelection();
                      }}
                      onChange={handleChangeText}
                      onChangeSelection={handleChangeTextSelection}
                      className={classNames(
                        '[&>.ql-toolbar]:!border-x-0',
                        '[&>.ql-toolbar]:!border-t',
                        '[&>.ql-toolbar]:!border-b',
                        '[&>.ql-toolbar]:!border-gray-300',
                        '[&>.ql-container]:!border-0',
                        '[&>.ql-container]:min-h-[250px]',
                        '[&>.ql-container>.ql-editor]:min-h-[250px]',
                      )}
                    />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <CustomCheckbox
                    checked={formik.values.bulk_generate_attachments}
                    onChange={event => handleToggleBulkGenerateReports(event.target.checked)}
                    className="select-none mr-0 leading-none"
                  />
                  <span className="text-sm leading-5 text-gray-900">
                    Auto-generate and attach personalised reports & certificates
                  </span>
                </div>
                {formik.values.bulk_generate_attachments ? (
                  <IssuerCommunicationGeneratedAttachmentsConfig
                    values={formik.values}
                    registerId={registerId}
                    selectedBulkReportClasses={selectedBulkReportClasses}
                    selectedBulkCertificateClasses={selectedBulkCertificateClasses}
                    selectedDividendEvent={selectedDividendEvent}
                    generatedAttachmentsError={generatedAttachmentsError}
                    onToggleGenerateReport={handleToggleGenerateReport}
                    onToggleGenerateCertificate={handleToggleGenerateCertificate}
                    onChangeReportType={handleChangeBulkReportType}
                    onChangeReportClasses={handleChangeBulkReportClasses}
                    onChangeReportDateRange={handleChangeBulkReportDateRange}
                    onChangeReportEndDate={handleChangeBulkReportEndDate}
                    onChangeReportFinancialYear={handleChangeBulkReportFinancialYear}
                    onChangeReportDividendEvent={handleChangeBulkReportDividendEvent}
                    onChangeReportFileType={handleChangeBulkReportFileType}
                    onChangeCertificateClasses={handleChangeBulkCertificateClasses}
                    onAsyncClasses={handleAsyncRegisterClassList}
                  />
                ) : (
                  <>
                    <ReportsSelectPaginate
                      label="Reports (Optional)"
                      onChange={setSelectedReports}
                      value={selectedReports}
                      // classNameSelect="z-[4]"
                    />
                    <CertificatesSelectPaginate
                      label="Certificates (Optional)"
                      value={selectedCertificates}
                      onChange={setSelectedCertificates}
                      // classNameSelect="z-[4]"
                    />
                  </>
                )}
                {securityWarning && (
                  <Alert icon={AlertBlueIcon} variant="neutral-2">
                    <div className="whitespace-normal">{securityWarning}</div>
                  </Alert>
                )}
                <Button
                  loading={
                    isRegistersLoading || isAnnouncementCreation || isCommunicationPreviewLoading
                  }
                  onClick={handlePrimaryAction}
                >
                  Next
                </Button>
              </div>
              <div className="space-y-4 lg:pl-6">
                <div className="flex gap-6">
                  <Select
                    label="REGISTER"
                    options={registerOptions}
                    value={registerId}
                    isLoading={isRegistersLoading || isClassHoldersLoading}
                    onChange={handleChangeRegister}
                    className="z-[3]"
                  />
                  <SelectPaginate
                    label="CLASS (Optional)"
                    fixedOption={allClassesOption}
                    cacheUniqs={[registerId]}
                    debounceTimeoutSearch={700}
                    onAsync={handleAsyncRegisterClassList}
                    // isLoading={(isRegistersLoading || isClassHoldersLoading) && !!selectedClass?.value}
                    value={selectedClass}
                    onChange={handleChangeClass}
                    className="z-[3]"
                  />
                </div>
                <div className="relative max-h-[450px] overflow-auto lg:max-h-[750px]">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 z-[2]">
                      <tr className="border-b bg-gray-100 text-gray-500 py-2 h-9">
                        <td className="w-11 pl-3">
                          <CustomCheckbox
                            checked={checkedAllHoldersCheckbox}
                            indeterminate={indeterminateAllHoldersCheckbox}
                            className={classNames(
                              '[&_.ant-checkbox-inner]:w-5',
                              '[&_.ant-checkbox-inner]:h-5',
                              '[&_.ant-checkbox-inner]:!rounded-sm',
                              '[&_span:not(.ant-checkbox-checked)>.ant-checkbox-inner]:bg-gray-100',
                            )}
                            onChange={e => handleToggleAllHoldersSelection(e.target.checked)}
                          />
                        </td>
                        <td>
                          {formik.values.shareholder_ids.length}{' '}
                          {pluralize('Holder', formik.values.shareholder_ids.length)}
                        </td>
                        <td className="pr-3">ID</td>
                      </tr>
                    </thead>
                    <tbody>
                      {registerHolders.map(holder => {
                        const ineligibilityReason = getHolderEmailIneligibilityReason(holder);

                        return (
                          <tr key={holder.id} className="h-[38px] [&:not(:last-child)]:border-b">
                            <td className="w-11 pl-3">
                              <CustomCheckbox
                                disabled={!!ineligibilityReason}
                                onChange={e =>
                                  handleToggleHolderSelection(holder.id, e.target.checked)
                                }
                                checked={formik.values.shareholder_ids.includes(holder.id)}
                                className="[&_.ant-checkbox-inner]:w-5 [&_.ant-checkbox-inner]:h-5 [&_.ant-checkbox-inner]:!rounded-sm"
                              />
                            </td>
                            <td>
                              <Link
                                to={{
                                  pathname: issuerDashboardPaths.dynamic.holder(
                                    holder.entity_id.toString(),
                                  ),
                                  search: params.toString(),
                                }}
                                className="text-blue-700 hover:underline"
                              >
                                {holder.entity.entity_name}
                              </Link>
                              {ineligibilityReason && (
                                <div className="ml-2 inline-flex items-center gap-1 text-xs text-gray-500">
                                  <span className="font-medium">Why?</span>
                                  <TooltipNew
                                    text={ineligibilityReason}
                                    positionStrategy="fixed"
                                    offset={8}
                                    noArrow
                                    classNameTooltip="!z-20 !max-w-[280px] !rounded-lg !border !border-gray-400 !bg-gray-100 !px-3 !py-2 !text-left !text-xs !leading-5 !text-gray-500"
                                  >
                                    <img
                                      alt="info"
                                      src={InfoCircleIcon}
                                      className="w-[18px] h-[18px] min-h-[18px] min-w-[18px]"
                                    />
                                  </TooltipNew>
                                </div>
                              )}
                            </td>
                            <td>{holder.entity.entity_id}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </form>
      </FormikProvider>
    </ModalContentWrapper>
  );
};
